import {
    ConsentLevel,
    isIE,
    ScriptLoadResult,
    useScriptWithConsent,
} from '@news-mono/web-common'
import React, { useState } from 'react'
import { LoggerContext } from '../../../diagnostics/LoggerContext'
import { ConsentFallback } from '../../../tracking/ConsentFallback/ConsentFallback'
import { TextLink } from '../../../typography/TextLink/TextLink'
import { tweetIDFromURl } from '../helpers/twitter'

interface CreateTweetOptions {
    conversation?: 'none' | 'all'
    cards?: 'hidden' | 'visible'
    width?: number | 'auto'
    align?: 'left' | 'right' | 'center' | undefined
    theme?: 'dark' | 'light'
}

export interface TwitterWidget {
    init: boolean
    ready: () => void
    widgets: {
        createTweet: (
            id: string,
            target: HTMLElement,
            opts?: CreateTweetOptions,
        ) => Promise<any>
    }
}

export declare const twttr: TwitterWidget

export interface TwitterEmbedProps {
    link: string
}

export const TwitterEmbed: React.FC<TwitterEmbedProps> = ({ link }) => {
    const logger = React.useContext(LoggerContext)
    const [loadedTweet, setLoadedTweet] = useState(false)
    const [failed, setFailed] = useState(
        typeof navigator !== 'undefined' && isIE(navigator),
    ) // Fail early for IE11 as twitter api isnt supported on IE1
    const container = React.useRef<HTMLDivElement>(null)

    let tweetId: string | false = false

    try {
        tweetId = tweetIDFromURl(link)
    } catch (err) {
        logger.warn({ link }, 'Unable to extract tweet ID')
    }

    const [result, error] = useScriptWithConsent({
        consentMet: {
            src: 'https://platform.twitter.com/widgets.js',
        },
        consentRequiredLevel: ConsentLevel.All,
    })

    React.useEffect(() => {
        if (
            result === ScriptLoadResult.Success &&
            tweetId &&
            container.current &&
            typeof twttr !== 'undefined'
        ) {
            try {
                twttr.widgets
                    .createTweet(tweetId, container.current)
                    .then(() => {
                        setLoadedTweet(true)
                    })
            } catch (error) {
                logger.debug('Failed to create Tweet')
                setFailed(true)
            }
        }
    }, [result, error, tweetId, logger])

    if (result === ScriptLoadResult.NotConsented) {
        return <ConsentFallback />
    }

    if (result === ScriptLoadResult.Failed || failed) {
        return (
            <div>
                <TextLink href={link} openInNewWindow={true}>
                    View Tweet
                </TextLink>
            </div>
        )
    }

    if (ScriptLoadResult.Loading || loadedTweet) {
        return (
            <div ref={container}>
                {!loadedTweet && (
                    <TextLink href={link} openInNewWindow={true}>
                        Loading Tweet
                    </TextLink>
                )}
            </div>
        )
    }

    return null
}

export default TwitterEmbed
